import { inject, Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ILoginData } from 'src/app/core/models/shared/Auth/ILoginData.interface';
import { ICompanyConfigResponse } from 'src/app/core/models/shared/Auth/ICompanyConfigResponse.interface';
import { ILoginResponseInterface } from 'src/app/core/models/shared/Auth/ILoginResponse.interface';
import { BasicConstants } from 'src/app/core/constants/basics-constant';
import { HttpPaths } from 'src/app/core/constants/http-paths';
import { CommonHttpService } from 'src/app/core/services/shared/common-http.service';
import { ChangeUserData } from 'src/app/core/Store/Auth/AuthAction';
import { Store } from '@ngrx/store';

import { environment as env } from 'src/environments/environment';
import { MenuService } from 'src/app/core/UIServices/menu.service';
import { MenuItem, SubMenuItem } from 'src/app/core/models/shared/menu.model';
import { Menu } from 'src/app/core/constants/menu';
import { IResponse } from 'src/app/shared/models/iresponse.interface';
import { toasterService } from 'src/app/core/UIServices/toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { RoutePermissionService } from 'src/app/core/services/shared/route-permission.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly CommonHttp = inject(CommonHttpService);
  // helper to build base URL: prefer companyLink from localStorage, fallback to env.apiUrl
  private getBaseLink(): string {
    const cl = localStorage.getItem('companyLink');
    if (cl && cl !== 'null') return cl;
    return env.apiUrl;
  }
  constructor(
    private readonly router: Router,
    private readonly toaster: toasterService,
    private readonly translate: TranslateService,
    private readonly Store: Store,
    private readonly menuService: MenuService,
    private readonly routePermissionService: RoutePermissionService
  ) { }

  isLoggedIn(): boolean {
    return !!localStorage.getItem(BasicConstants.USER_TOKEN);
  }

  CheckCompanyExistance(LoginData: ILoginData): Observable<ICompanyConfigResponse> {
    // Query params: code, applicationName
    const params = {
      code: LoginData.CompanyCode,
      applicationName: LoginData.ApplicationName
    };
    return this.CommonHttp.CommonPostRequestsWithQuery(`${env.apiUrl}${HttpPaths.API_COMPANYCONFIG_URL}`, params, null).pipe(
      map((data: any) => {
        let CompanyConfig: ICompanyConfigResponse;
        CompanyConfig = data.data;
        return CompanyConfig;
      })
    );
  }

  CheckCompanyExistanceAtAdminTool(LoginData: ILoginData): Observable<ICompanyConfigResponse> {
    // Query params: code, applicationName
    const params = {
      CompanyCode: LoginData.CompanyCode,
      ApplicationName: LoginData.ApplicationName
    };
    return this.CommonHttp.CommonGetRequestsWithQuery(`${env.apiUrl}${HttpPaths.API_COMPANYCONFIG_ADMIN_TOOL_URL}`, params).pipe(
      map((data: any) => {
        let CompanyConfig: ICompanyConfigResponse;
        CompanyConfig = data.data;
        return CompanyConfig;
      })
    );
  }


  Login(LoginData: ILoginData, companyLink: string) {
    if (LoginData?.CompanyCode) {
      localStorage.setItem(BasicConstants.COMPANY_CODE, LoginData.CompanyCode);
    }
    return this.CommonHttp.CommonPostRequests(LoginData, `${companyLink}${HttpPaths.API_LOGIN_URL}`).pipe(
      map((items) => {
        return items?.data ? items.data as ILoginResponseInterface : items as ILoginResponseInterface;
      }),
      catchError((err) => {
        let LoginResponse: ILoginResponseInterface = { success: 'false', token: '' };
        return of(LoginResponse);
      }),
    );
  }

  getRefreshToken() {
    let userName: string | null = localStorage.getItem(BasicConstants.USER_NAME);
    let refreshToken: string | null = localStorage.getItem(BasicConstants.USER_REFRESH_TOKEN);
    let companyCode: string | null = localStorage.getItem(BasicConstants.COMPANY_CODE);
    const payload = {
      companyCode: companyCode,
      userName: userName,
      refreshToken: refreshToken,
      applicationName: 'Web'
    };
    return this.CommonHttp.CommonPostRequests(payload, `${localStorage.getItem('companyLink')}${HttpPaths.API_REFRESH_TOKEN}`).pipe(
      map((res) => {
        const token = res?.token || res?.Token;

        if (!token) {
          return handleRefreshFailure(this.toaster, this.router, this.translate);
        }

        // Store new tokens
        localStorage.setItem(BasicConstants.USER_TOKEN, token);
        if (res?.refreshToken) {
          localStorage.setItem(BasicConstants.USER_REFRESH_TOKEN, res.refreshToken);
        }
        this.routePermissionService.clearUserPermissionsCache();
        return res;
      }));
  }

  logout(userId: string) {
    return this.CommonHttp.CommonPostRequests(null, `${localStorage.getItem('companyLink')}${HttpPaths.API_LOGINWITH_USERID}${userId}`).subscribe({
      next: (res) => {
        localStorage.removeItem(BasicConstants.USER_TOKEN);
        localStorage.removeItem(BasicConstants.USER_REFRESH_TOKEN);
        localStorage.removeItem(BasicConstants.USER_ID);
        localStorage.removeItem(BasicConstants.USER_NAME);
        localStorage.removeItem(BasicConstants.COMPANY_CODE);
        this.routePermissionService.clearCache();
        this.router.navigate(['/auth/login'], { queryParams: {} });
      },
      error: (err) => {
        localStorage.removeItem(BasicConstants.USER_TOKEN);
        localStorage.removeItem(BasicConstants.USER_REFRESH_TOKEN);
        localStorage.removeItem(BasicConstants.USER_ID);
        localStorage.removeItem(BasicConstants.USER_NAME);
        localStorage.removeItem(BasicConstants.COMPANY_CODE);
        this.routePermissionService.clearCache();
        this.router.navigate(['/auth/login'], { queryParams: {} });
      },
    });
  }

  private convertApiMenuToMenuItems(apiMenu: any[]): MenuItem[] {
    /* -----------------------------------------------------------
     * 1. Build a Set with the permissions coming from the JWT
     * --------------------------------------------------------- */
    const token = localStorage.getItem(BasicConstants.USER_TOKEN);
    const userPermissions = new Set<string>();

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (Array.isArray(payload.Permission)) {
          payload.Permission.forEach((p: string) => userPermissions.add(p));
        }
      } catch { }
    }

    /* -----------------------------------------------------------
     * 2. Helper functions to extract logic
     * --------------------------------------------------------- */
    const extractLabel = (name: any): string => {
      if (typeof name === 'string') return name;
      if (name && typeof name === 'object') {
        return name.name || name.title || name.label || JSON.stringify(name);
      }
      return String(name || '');
    };

    const extractRoute = (node: any): string | null => {
      return node.route && node.route.trim() !== '' ? node.route : null;
    };

    const extractIcon = (node: any): string => {
      return node.icon && node.icon.trim() !== '' ? node.icon : '';
    };

    const processChildren = (node: any, convert: (n: any) => Partial<SubMenuItem> | null): SubMenuItem[] => {
      const children: SubMenuItem[] = [];
      if (Array.isArray(node.childNode) && node.childNode.length > 0) {
        for (const child of node.childNode) {
          const converted = convert(child);
          if (converted) {
            children.push(converted as SubMenuItem);
          }
        }
      }
      return children;
    };

    /* -----------------------------------------------------------
     * 3. Recursive converter with permission check
     * --------------------------------------------------------- */
    const convert = (node: any): Partial<SubMenuItem> | null => {
      // Skip deleted or nodes without name
      if (!node?.name || node.isDeleted) return null;

      /* ---- permission check ---- */
      // if (node.permission && node.permission.trim() !== '' && !userPermissions.has(node.permission)) {
      //   return null;
      // }

      /* ---- process children recursively ---- */
      const children = processChildren(node, convert);

      /* ---- extract node properties ---- */
      const label = extractLabel(node.name);
      const route = extractRoute(node);
      const icon = extractIcon(node);

      /* ---- if no route and no children after filtering, skip this node ---- */
      if (!route && children.length === 0) return null;

      // if (!route && children.length === 1) return children[0];

      return {
        icon: icon,
        label: label,
        route: route,
        expanded: false,
        active: false,
        children: children.length > 0 ? children : undefined,
      };
    };

    /* -----------------------------------------------------------
     * 4. Root processing - skip the "Screen" wrapper
     * --------------------------------------------------------- */
    let rootNodes = apiMenu;

    // If the first node is the "Screen" wrapper, unwrap it
    if (
      apiMenu.length > 0 &&
      (apiMenu[0].name === 'الشاشة' || apiMenu[0].name === 'Screen') &&
      Array.isArray(apiMenu[0].childNode)
    ) {
      rootNodes = apiMenu[0].childNode;
    }

    // Convert all root nodes
    const items = rootNodes
      .map(convert)
      .filter(Boolean) as SubMenuItem[];

    return items.length ? [{ group: 'Main', items }] : [];
  }

  public userData$ = new BehaviorSubject<any>(null);

  getUserByToken(): Observable<any> {
    if (localStorage.getItem(BasicConstants.USER_TOKEN)) {
      return this.CommonHttp.CommonGetRequests(
        `${localStorage.getItem('companyLink')}${HttpPaths.API_GET_USER_DATA}`,
      ).pipe(
        map((user: any) => {
          if (user) {
            this.routePermissionService.clearCache();

            localStorage.setItem(BasicConstants.USER_ID, user.data.userId);
            if (user.data?.companyCode) {
              localStorage.setItem(BasicConstants.COMPANY_CODE, user.data.companyCode);
            }
            localStorage.setItem(BasicConstants.USER_DATA, JSON.stringify(user.data));
            this.userData$.next(user.data);
            this.Store.dispatch(ChangeUserData(user.data));

            // Use dynamic menu from API
            if (user.data.menu && Array.isArray(user.data.menu)) {
              this.menuService.setPagesMenu(
                this.convertApiMenuToMenuItems(user.data.menu),
              );
            } else {
              // Fallback to static menu if no menu data from API
              this.menuService.setPagesMenu(
                user.data?.isVendor ? Menu.vendorPages : Menu.pages,
              );
            }
          } else {
            this.logout(localStorage.getItem(BasicConstants.USER_ID) ?? '');
          }
        }),
      );
    } else {
      return of(null);
    }
  }

  changePassword(model: any) {
    return this.CommonHttp.CommonPutRequests(model, `${localStorage.getItem('companyLink')}${HttpPaths.API_CHANGE_PASSWORD}`)
  }

  forgetPassword(email: string) {
    const payload = { email: email };
    const base = this.getBaseLink();
    return this.CommonHttp.CommonPostRequests(payload, `${base}${HttpPaths.API_FORGET_PASSWORD}`);
  }

  ValidateOTP(email: string, otp: string) {
    const payload = { email: email, otp: otp };
    const base = this.getBaseLink();
    return this.CommonHttp.CommonPostRequests(payload, `${base}${HttpPaths.API_VALIDATE_OTP}`);
  }

  ConfirmForgetPassword(email: string, resetToken: string, newPassword: string, confirmPassword: string) {
    const payload = { email: email, resetToken: resetToken, newPassword: newPassword, confirmPassword: confirmPassword };
    const base = this.getBaseLink();
    return this.CommonHttp.CommonPutRequests(payload, `${base}${HttpPaths.API_CONFIRM_FORGET_PASSWORD}`);
  }

  checkUserNameExist(model: any): Observable<IResponse<boolean>> {
    return this.CommonHttp.CommonGetRequestsWithQuery(`${localStorage.getItem("companyLink")}${HttpPaths.API_CHECK_USER_USERNAME_EXIST}`, model);
  }

  checkUserPhoneExist(model: any): Observable<IResponse<boolean>> {
    return this.CommonHttp.CommonGetRequestsWithQuery(`${localStorage.getItem("companyLink")}${HttpPaths.API_CHECK_USER_PHONE_EXIST}`, model);
  }

  checkUserEmailExist(model: any): Observable<IResponse<boolean>> {
    return this.CommonHttp.CommonGetRequestsWithQuery(`${localStorage.getItem("companyLink")}${HttpPaths.API_CHECK_USER_EMAIL_EXIST}`, model);
  }

  isExistUserName: boolean = false;
  isExistPhone: boolean = false;
  isExistEmail: boolean = false;

  onBlurUserName(userName: string | undefined, id: string | undefined) {
    if (!userName) return;
    let payload = {
      id: id || 0,
      propertyValue: userName
    };
    this.checkUserNameExist(payload).subscribe({
      next: (res) => {
        if (res.isSuccess && res.isExists) {
          this.toaster.warning(res.message);
          this.isExistUserName = true;
        } else {
          this.isExistUserName = false;
        }
      },
      error: (err) => {
        this.toaster.error(err.error?.Message || err.message || 'Unexpected error');
      }
    });
  }

  onBlurUserPhone(phoneNumber: string | undefined, id: string | undefined) {
    if (!phoneNumber) return;
    let payload = {
      id: id || 0,
      propertyValue: phoneNumber
    };
    this.checkUserPhoneExist(payload).subscribe({
      next: (res) => {
        if (res.isSuccess && res.isExists) {
          this.toaster.warning(res.message);
          this.isExistPhone = true;
        } else {
          this.isExistPhone = false;
        }
      },
      error: (err) => {
        this.toaster.error(err.error?.Message || err.message || 'Unexpected error');
      }
    });
  }

  onBlurUserEmail(email: string | undefined, id: string | undefined) {
    if (!email) return;
    let payload = {
      id: id || 0,
      propertyValue: email
    };
    this.checkUserEmailExist(payload).subscribe({
      next: (res) => {
        if (res.isSuccess && res.isExists) {
          this.toaster.warning(res.message);
          this.isExistEmail = true;
        } else {
          this.isExistEmail = false;
        }
      },
      error: (err) => {
        this.toaster.error(err.error?.Message || err.message || 'Unexpected error');
      }
    });
  }

  getListOfUsersWithEmployeeName(companyId: number) {
    let payload = {
      companyId: companyId
    };
  return this.CommonHttp.CommonPostRequests(
    payload,
    `${localStorage.getItem('companyLink')}${HttpPaths.API_GET_USERS_WITH_EMPLOYEE_NAME}`
  );
}

}
function handleRefreshFailure(toastr: any, router: any, translate: any): any {
  throw new Error('Function not implemented.');
}


